import { useState, useEffect, useCallback } from 'react';

type SetValue<T> = T | ((val: T) => T);

/**
 * LocalStorage管理用カスタムフック
 * 型安全なLocalStorageの読み書きを提供
 */
export const useLocalStorage = <T>(
  key: string, 
  initialValue: T
): [T, (value: SetValue<T>) => void, () => void] => {
  // 初期値の取得
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error reading localStorage key:', error);
      }
      return initialValue;
    }
  });

  // 値の設定
  const setValue = useCallback((value: SetValue<T>) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error setting localStorage key:', error);
      }
    }
  }, [key, storedValue]);

  // 値の削除
  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      window.localStorage.removeItem(key);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error removing localStorage key:', error);
      }
    }
  }, [key, initialValue]);

  // storageイベントのリスニング（他のタブでの変更を検知）
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error parsing localStorage key:', error);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue, removeValue];
}; 